import { useState, useCallback, useRef, useEffect } from 'react'
import {
  Wifi, WifiOff, RefreshCw, ChevronDown, ChevronUp, Check,
  Zap, Loader2, X, AlertTriangle,
} from 'lucide-react'
import { JourneyVisualization, StepDetailDrawer } from './components/JourneyVisualization'
import { InlineWebhookPanel } from './components/InlineWebhookPanel'
import {
  PayloadEditor, CopyButton, RawJsonCollapsible,
  CurlCollapsible, SectionLabel, ResultBadge, ComboSelect, inputCls, labelCls,
  EndpointPicker,
} from './components/shared'
import type { EndpointDef } from './components/shared'
import { KYC_SCENARIOS, BLANK_FORM, DEFAULT_CREDS } from './data'
import {
  proxyUrl, buildHeaders, buildIndividual, scenarioToForm, outcomeColors,
} from './utils'
import type { Creds, IndividualForm, LogEntry } from './types'

// ── Endpoint definitions ──────────────────────────────────────────────────────

const KYC_V2_ENDPOINTS: EndpointDef[] = [
  { id: 'create-execute', method: 'POST', label: 'Create + Execute', path: '/individuals/new/serviceprofiles/{service}/workflows/{workflow}/execute', category: 'Workflows', core: true },
  { id: 'create', method: 'POST', label: 'Create Entity', path: '/individuals', category: 'Entity Management', core: true },
  { id: 'execute', method: 'POST', label: 'Execute Workflow', path: '/individuals/{entityId}/serviceprofiles/{service}/workflows/{workflow}/execute', category: 'Workflows', core: true },
  { id: 'get-result', method: 'GET', label: 'Workflow Results', path: '/individuals/{entityId}/serviceprofiles/{service}/workflows/{workflow}/executions/{executionId}', category: 'Workflows', core: true },
  { id: 'get-entity', method: 'GET', label: 'Entity Details', path: '/individuals/{entityId}', category: 'Entity Management', core: true },
  // Entity Management
  { id: 'update-entity', method: 'PATCH', label: 'Update Individual', path: '/individuals/{entityId}', category: 'Entity Management' },
  { id: 'delete-entity', method: 'DELETE', label: 'Delete Individual', path: '/individuals/{entityId}', category: 'Entity Management' },
  // Workflows
  { id: 'list-workflows', method: 'GET', label: 'List Workflows', path: '/individuals/{entityId}/serviceprofiles/{service}/workflows', category: 'Workflows' },
  { id: 'list-executions', method: 'GET', label: 'List Executions', path: '/individuals/{entityId}/serviceprofiles/{service}/workflows/{workflow}/executions', category: 'Workflows' },
  { id: 'override-status', method: 'PATCH', label: 'Override Execution Status', path: '/individuals/{entityId}/serviceprofiles/{service}/workflows/{workflow}/executions/{executionId}', category: 'Workflows' },
  // Profiles
  { id: 'get-profile', method: 'GET', label: 'Get Service Profile', path: '/individuals/{entityId}/serviceprofiles/{service}', category: 'Profiles' },
  { id: 'update-profile', method: 'PATCH', label: 'Update Service Profile', path: '/individuals/{entityId}/serviceprofiles/{service}', category: 'Profiles' },
  // Results
  { id: 'get-results', method: 'GET', label: 'Get Process Results', path: '/individuals/{entityId}/results', category: 'Results' },
  { id: 'get-result-single', method: 'GET', label: 'Get Single Result', path: '/individuals/{entityId}/results/{resultId}', category: 'Results' },
  { id: 'approve-mkyc', method: 'POST', label: 'Approve Manual KYC', path: '/individuals/{entityId}/results/mkyc', category: 'Results' },
  { id: 'update-aml-status', method: 'PATCH', label: 'Update AML Status', path: '/individuals/{entityId}/results/aml', category: 'Results' },
  { id: 'update-idv-status', method: 'PATCH', label: 'Update IDV Status', path: '/individuals/{entityId}/results/idv', category: 'Results' },
  { id: 'update-fraud-status', method: 'PATCH', label: 'Update Fraud Status', path: '/individuals/{entityId}/results/fraud', category: 'Results' },
  { id: 'update-dup-status', method: 'PATCH', label: 'Update Duplicate Status', path: '/individuals/{entityId}/results/duplicate', category: 'Results' },
  { id: 'update-matchlist-status', method: 'PATCH', label: 'Update Matchlist Status', path: '/individuals/{entityId}/results/matchlist', category: 'Results' },
  // Documents
  { id: 'get-documents', method: 'GET', label: 'Get All Documents', path: '/individuals/{entityId}/documents', category: 'Documents' },
  { id: 'create-document', method: 'POST', label: 'Create Document', path: '/individuals/{entityId}/documents', category: 'Documents' },
  { id: 'get-document', method: 'GET', label: 'Get Document', path: '/individuals/{entityId}/documents/{documentId}', category: 'Documents' },
  { id: 'update-document', method: 'PATCH', label: 'Update Document', path: '/individuals/{entityId}/documents/{documentId}', category: 'Documents' },
  { id: 'delete-document', method: 'DELETE', label: 'Delete Document', path: '/individuals/{entityId}/documents/{documentId}', category: 'Documents' },
  // IDV
  { id: 'idv-token', method: 'POST', label: 'Get IDV Token', path: '/individuals/{entityId}/actions/idv/token', category: 'IDV' },
  { id: 'idv-process', method: 'POST', label: 'Process IDV Results', path: '/individuals/{entityId}/actions/idv/process', category: 'IDV' },
  // Monitoring
  { id: 'toggle-monitoring', method: 'PATCH', label: 'Toggle Monitoring', path: '/individuals/{entityId}/monitoring', category: 'Monitoring' },
  // Search
  { id: 'search-profiles', method: 'POST', label: 'Search Profiles', path: '/search/serviceprofiles', category: 'Search' },
]

// ── helpers ───────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>

function resultDotColor(r: 'PASS' | 'FAIL' | 'REVIEW') {
  if (r === 'PASS') return '#34d399'
  if (r === 'FAIL') return '#f87171'
  return '#fb923c'
}

function outcomeLabel(wr: AnyObj | null): string {
  if (!wr) return ''
  const s = (wr.workflowExecutionState ?? wr.overallRiskLevel ?? '').toUpperCase()
  if (wr.issues?.length || s === 'TIMEOUT' || s === 'ERROR') return 'FAIL'
  const rs = (wr.checkSummary?.overallRiskLevel ?? wr.riskAssessment?.riskLevel ?? s)
  if (rs === 'LOW' || rs === 'CLEAR' || rs === 'PASS') return 'PASS'
  if (rs === 'MEDIUM' || rs === 'REFER' || rs === 'REVIEW') return 'REFER'
  if (rs === 'HIGH' || rs === 'FAIL') return 'FAIL'
  return s || 'DONE'
}

// Parse "PASS — JAMES TESTONE (Driver's License)" → { name: "James Testone", badge: "PASS", docType: "Driver's License" }
// Parse "REVIEW — PEP — JAMES G TESTELEVEN" → { name: "James G Testeleven", badge: "PEP", docType: null }
function parsePersonaChip(label: string): { name: string; badge: string; docType: string | null } {
  const resultMatch = label.match(/^(PASS|FAIL|REVIEW)\s*—\s*/i)
  const badge0 = resultMatch?.[1]?.toUpperCase() ?? 'PASS'
  let rest = resultMatch ? label.slice(resultMatch[0].length) : label
  const catMatch = rest.match(/^(PEP\+Sanction|PEP|Sanction)\s*—\s*/i)
  const badge = catMatch ? catMatch[1] : badge0
  if (catMatch) rest = rest.slice(catMatch[0].length)
  const docTypeMatch = rest.match(/\s*\(([^)]+)\)\s*$/)
  const docType = docTypeMatch ? docTypeMatch[1] : null
  const rawName = rest.replace(/\s*\([^)]+\)\s*$/, '').trim()
  const name = rawName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
  return { name, badge, docType }
}

// ── sub-components ────────────────────────────────────────────────────────────

// KYC check result rows
function KycCheckSummary({ wr }: { wr: AnyObj }) {
  const checks = (wr.checkSummary?.checkResults ?? wr.workflowStepResults ?? []) as AnyObj[]
  if (!checks.length) return null
  return (
    <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
      <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #1a2740', background: '#0a1322' }}>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">KYC Result Summary</p>
      </div>
      <div className="divide-y" style={{ borderColor: '#1a2740' }}>
        {checks.map((c, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-2">
            <span className="text-[11px] text-[#94a3b8] flex-1 font-mono">{c.checkType ?? c.stepName ?? '—'}</span>
            <ResultBadge result={c.result ?? c.overallResult ?? '—'} />
          </div>
        ))}
      </div>
    </div>
  )
}

// Risk factors
function RiskFactorsPanel({ wr }: { wr: AnyObj }) {
  const risk = wr.riskAssessment
  if (!risk) return null
  const factors = (risk.riskFactors ?? []) as AnyObj[]
  // Sort highest score first, scale bars relative to max score
  const sorted = [...factors].sort((a, b) => ((b.score as number) ?? 0) - ((a.score as number) ?? 0))
  // Scale bars against total risk score (or 100 minimum) so small scores stay visually small
  const totalScore = typeof risk.riskScore === 'number' ? risk.riskScore : Math.max(...sorted.map(f => (f.score as number) ?? 0), 1)
  const barMax = Math.max(totalScore, 100)
  const riskLevel = (risk.riskLevel as string | undefined)?.toUpperCase() ?? ''
  const scoreColor = riskLevel === 'LOW' || riskLevel === 'CLEAR' ? '#34d399'
    : riskLevel === 'MEDIUM' ? '#fb923c'
    : riskLevel === 'HIGH' || riskLevel === 'UNACCEPTABLE' ? '#f87171'
    : '#94a3b8'
  return (
    <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: '1px solid #1a2740', background: '#0a1322' }}>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">Risk Factors</p>
        {risk.riskScore != null && (
          <span className="text-[10px] font-mono text-[#94a3b8]">
            Total score: <span className="font-bold" style={{ color: scoreColor }}>{risk.riskScore}</span>
            {riskLevel && <span className="ml-1.5 font-semibold" style={{ color: scoreColor }}>· {riskLevel}</span>}
          </span>
        )}
      </div>
      <div className="p-3 space-y-2">
        {sorted.length === 0 ? (
          <p className="text-[11px] text-[#475569] italic">No risk factors</p>
        ) : sorted.map((f, i) => {
          const score = typeof f.score === 'number' ? f.score : 0
          const name = (f.description as string | undefined) ?? (f.factor as string | undefined) ?? '—'
          const value = f.value !== undefined && f.value !== '' ? String(f.value) : null
          const barPct = (score / barMax) * 100
          const barColor = barPct < 5 ? '#34d399' : barPct < 20 ? '#fb923c' : '#f87171'
          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-[#94a3b8] flex-1 mr-2">{name}</span>
                <div className="flex items-center gap-2 shrink-0">
                  {value && (
                    <span className="text-[9px] font-mono text-[#5b6b85] px-1 py-0.5 rounded" style={{ background: '#0a1322' }}>
                      {value}
                    </span>
                  )}
                  <span className="text-[10px] font-mono font-bold w-6 text-right" style={{ color: score > 0 ? barColor : '#3f4f6b' }}>{score}</span>
                </div>
              </div>
              {score > 0 && (
                <div className="h-[4px] rounded-full" style={{ background: '#16223a' }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${barPct}%`, background: barColor }}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Issues panel
function IssuesPanel({ wr }: { wr: AnyObj }) {
  const issues = (wr.issues ?? []) as AnyObj[]
  if (!issues.length) return null
  return (
    <div className="rounded-[11px] p-3 space-y-1.5" style={{ border: '1px solid rgba(248,113,113,.3)', background: 'rgba(248,113,113,.06)' }}>
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={13} className="text-red-400" />
        <p className="text-[11px] font-semibold text-red-400">Issues / Blocked Reasons</p>
      </div>
      {issues.map((issue, i) => (
        <div key={i} className="text-[11px] text-[#fca5a5] font-mono">
          {typeof issue === 'string' ? issue : JSON.stringify(issue)}
        </div>
      ))}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

interface KycV2WorkspaceProps {
  creds: Creds
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
}

export function KycV2Workspace({ creds, addLog }: KycV2WorkspaceProps) {
  const apiBase = creds.apiBase || DEFAULT_CREDS.apiBase
  const hasCreds = !!(creds.customerId && creds.apiKey)

  // Endpoint picker state
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('create-execute')

  // Request state
  const [connStatus, setConnStatus] = useState<'idle' | 'ok' | 'error'>('idle')
  const [workflows, setWorkflows] = useState<{ serviceName: string; workflowName: string }[]>([])
  const [selectedWorkflow, setSelectedWorkflow] = useState('')
  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [form, setForm] = useState<IndividualForm>(BLANK_FORM)
  const [rawPayloadOverride, setRawPayloadOverride] = useState<{ individual: Record<string, unknown> } | null>(null)
  const [formOpen, setFormOpen] = useState(true)

  // GET endpoint inputs
  const [getEntityIdInput, setGetEntityIdInput] = useState('')
  const [getExecutionIdInput, setGetExecutionIdInput] = useState('')

  // Execution state
  const [loading, setLoading] = useState<string | null>(null)
  const [pollAttempt, setPollAttempt] = useState(0)
  const selectedWorkflowRef = useRef(selectedWorkflow)
  selectedWorkflowRef.current = selectedWorkflow

  // Result state
  const [entityId, setEntityId] = useState<string | null>(null)
  const [executionId, setExecutionId] = useState<string | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [workflowResult, setWorkflowResult] = useState<any | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const [selectedStep, setSelectedStep] = useState<string | null>(null)
  const [webhookReceived, setWebhookReceived] = useState(false)

  // Auto-populate GET inputs from prior results
  useEffect(() => { if (entityId) setGetEntityIdInput(entityId) }, [entityId])
  useEffect(() => { if (executionId) setGetExecutionIdInput(executionId) }, [executionId])

  const individualPayload = rawPayloadOverride ?? buildIndividual(form)
  const createAndExecPayload = { workflowName: selectedWorkflow, ...individualPayload }
  const execEndpoint = `/individuals/new/serviceprofiles/DEFAULT/workflows/${selectedWorkflow || '{workflow}'}/execute`

  // Payload editor state
  const [editorText, setEditorText] = useState(() => JSON.stringify(createAndExecPayload, null, 2))
  const [editorDirty, setEditorDirty] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)

  // Sync derived payload → editor whenever form/persona/workflow changes (unless user has edited)
  const derivedJson = JSON.stringify(createAndExecPayload)
  useEffect(() => {
    if (!editorDirty) {
      setEditorText(JSON.stringify(JSON.parse(derivedJson), null, 2))
      setEditorError(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedJson, editorDirty])

  // Reset editor dirty state when switching endpoints
  function handleEndpointSelect(id: string) {
    setSelectedEndpoint(id)
    setEditorDirty(false)
    setEditorError(null)
  }

  function handleEditorChange(v: string) {
    setEditorText(v)
    setEditorDirty(true)
    setEditorError(null)
  }

  function resetEditor() {
    setEditorDirty(false)
    setEditorText(JSON.stringify(createAndExecPayload, null, 2))
    setEditorError(null)
  }

  function getEditorPayload(): Record<string, unknown> | null {
    try {
      return JSON.parse(editorText) as Record<string, unknown>
    } catch {
      setEditorError('Invalid JSON — fix syntax before sending')
      return null
    }
  }

  function updateForm(updater: (f: IndividualForm) => IndividualForm) {
    setRawPayloadOverride(null)
    setEditorDirty(false)
    setForm(updater)
  }

  function selectPersona(idx: number) {
    setScenarioIdx(idx)
    setForm(scenarioToForm(KYC_SCENARIOS[idx]))
    setRawPayloadOverride(KYC_SCENARIOS[idx].rawPayload)
    setEditorDirty(false)
    setEditorError(null)
    // clear prior result
    setEntityId(null)
    setWorkflowResult(null)
    setFullResult(null)
    setSelectedStep(null)
    setPollAttempt(0)
  }

  const doFetch = useCallback(async (method: string, path: string, body: unknown | undefined, annotation: string) => {
    const url = `${apiBase}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(creds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }, [apiBase, creds, addLog])

  async function testConnection() {
    setLoading('conn')
    try {
      await doFetch('GET', '/kyc/ruok', undefined, 'Health check — verifies API credentials')
      setConnStatus('ok')
    } catch {
      setConnStatus('error')
    } finally {
      setLoading(null)
    }
  }

  const loadWorkflows = useCallback(async () => {
    setLoading('wf')
    try {
      const data = await doFetch('GET', '/workflows', undefined, 'List configured KYC workflows') as { workflows?: { serviceName: string; workflowName: string }[] }
      const wfs = data?.workflows ?? []
      setWorkflows(wfs)
      if (wfs.length > 0) setSelectedWorkflow(wfs[0].workflowName)
    } catch { /* logged */ } finally {
      setLoading(null)
    }
  }, [doFetch])

  // Auto-load workflows whenever the active customer changes
  const lastAutoLoadedCustomer = useRef('')
  useEffect(() => {
    if (hasCreds && creds.customerId !== lastAutoLoadedCustomer.current) {
      lastAutoLoadedCustomer.current = creds.customerId
      void loadWorkflows()
    }
  }, [hasCreds, creds.customerId, loadWorkflows])

  async function pollResult(eid: string, wfeId: string, wf: string) {
    for (let i = 0; i < 20; i++) {
      setPollAttempt(i + 1)
      try {
        const data = await doFetch(
          'GET',
          `/individuals/${eid}/serviceprofiles/DEFAULT/workflows/${wf}/executions/${wfeId}`,
          undefined,
          `Poll KYC result (attempt ${i + 1}/20)`,
        ) as { workflowResult?: { workflowExecutionState?: string } }
        const state = data?.workflowResult?.workflowExecutionState
        if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') {
          setWorkflowResult(data.workflowResult)
          setFullResult(data)
          return
        }
      } catch { /* keep polling */ }
      await new Promise(r => setTimeout(r, 4000))
    }
    setPollAttempt(0)
  }

  async function createAndExecute() {
    if (!selectedWorkflow) return
    const payload = getEditorPayload()
    if (!payload) return
    const wf = selectedWorkflow
    setLoading('exec')
    setWorkflowResult(null); setFullResult(null); setEntityId(null); setSelectedStep(null); setPollAttempt(0)
    try {
      const data = await doFetch('POST', `/individuals/new/serviceprofiles/DEFAULT/workflows/${wf}/execute`, payload, 'Create individual + execute KYC in one call') as {
        individual?: { entityId?: string }
        workflowResult?: { workflowExecutionState?: string; workflowExecutionId?: string }
      }
      const eid = data?.individual?.entityId
      if (eid) setEntityId(eid)
      const wr = data?.workflowResult
      if (wr?.workflowExecutionId) setExecutionId(wr.workflowExecutionId)
      const state = wr?.workflowExecutionState
      if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') {
        setWorkflowResult(wr); setFullResult(data)
      } else if (wr?.workflowExecutionId && eid) {
        await pollResult(eid, wr.workflowExecutionId, wf)
      } else {
        setWorkflowResult(wr ?? null); setFullResult(data)
      }
    } catch { /* logged */ } finally {
      setLoading(null); setPollAttempt(0)
    }
  }

  async function createOnly() {
    const payload = getEditorPayload()
    if (!payload) return
    setLoading('create')
    try {
      const data = await doFetch('POST', '/individuals', payload, 'Create individual entity (no workflow)') as { individual?: { entityId?: string } }
      if (data?.individual?.entityId) setEntityId(data.individual.entityId)
    } catch { /* logged */ } finally {
      setLoading(null)
    }
  }

  async function executeOnExisting() {
    const eid = getEntityIdInput || entityId
    if (!eid || !selectedWorkflow) return
    const wf = selectedWorkflow
    setLoading('execwf')
    setWorkflowResult(null); setFullResult(null); setSelectedStep(null); setPollAttempt(0)
    try {
      const data = await doFetch('POST', `/individuals/${eid}/serviceprofiles/DEFAULT/workflows/${wf}/execute`, { workflowName: wf }, 'Execute KYC workflow on existing entity') as {
        workflowResult?: { workflowExecutionState?: string; workflowExecutionId?: string }
      }
      const wr = data?.workflowResult
      if (wr?.workflowExecutionId) setExecutionId(wr.workflowExecutionId)
      const state = wr?.workflowExecutionState
      if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') {
        setWorkflowResult(wr); setFullResult(data)
      } else if (wr?.workflowExecutionId && eid) {
        await pollResult(eid, wr.workflowExecutionId, wf)
      }
    } catch { /* logged */ } finally {
      setLoading(null); setPollAttempt(0)
    }
  }

  async function fetchWorkflowResult() {
    const eid = getEntityIdInput || entityId
    const wfeId = getExecutionIdInput || executionId
    if (!eid || !wfeId || !selectedWorkflow) return
    setLoading('getresult')
    try {
      const data = await doFetch('GET', `/individuals/${eid}/serviceprofiles/DEFAULT/workflows/${selectedWorkflow}/executions/${wfeId}`, undefined, 'Get KYC workflow execution result') as {
        workflowResult?: { workflowExecutionState?: string }
      }
      if (data?.workflowResult) {
        setWorkflowResult(data.workflowResult)
        setFullResult(data)
      }
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function getEntityDetails() {
    const eid = getEntityIdInput || entityId
    if (!eid) return
    setLoading('getentity')
    try {
      const data = await doFetch('GET', `/individuals/${eid}`, undefined, 'Get individual entity details')
      setFullResult(data)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  const isRunning = loading === 'exec' || loading === 'execwf'
  const outcome = workflowResult ? outcomeLabel(workflowResult) : ''
  const oc = outcomeColors(outcome)

  // Extract steps from workflowResult
  const steps = workflowResult?.steps ?? {}
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stepResults: any[] = workflowResult?.workflowStepResults ?? []
  const wfeId = (workflowResult?.workflowExecutionId as string | undefined) ?? null

  // Determine the selected endpoint definition
  const epDef = KYC_V2_ENDPOINTS.find(e => e.id === selectedEndpoint) ?? KYC_V2_ENDPOINTS[0]
  const isGetEndpoint = epDef.method === 'GET'

  // Dynamic header path for selected endpoint
  const dynamicPath = selectedEndpoint === 'create-execute' ? execEndpoint
    : selectedEndpoint === 'create' ? '/individuals'
    : selectedEndpoint === 'execute' ? `/individuals/${getEntityIdInput || '{entityId}'}/serviceprofiles/DEFAULT/workflows/${selectedWorkflow || '{workflow}'}/execute`
    : selectedEndpoint === 'get-result' ? `/individuals/${getEntityIdInput || '{entityId}'}/serviceprofiles/DEFAULT/workflows/${selectedWorkflow || '{workflow}'}/executions/${getExecutionIdInput || '{executionId}'}`
    : `/individuals/${getEntityIdInput || '{entityId}'}`

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: '200px minmax(350px, 400px) 1fr', alignItems: 'start', padding: '18px 0' }}
    >
      {/* ── COLUMN 1: Endpoint Picker ── */}
      <div className="space-y-3">
        <p className="text-[9px] font-bold uppercase tracking-wider text-[#3f4f6b] px-1">Endpoints</p>
        <EndpointPicker
          endpoints={KYC_V2_ENDPOINTS}
          selectedId={selectedEndpoint}
          onSelect={handleEndpointSelect}
        />
      </div>

      {/* ── COLUMN 2: Form Panel ── */}
      <div
        className="rounded-[14px] overflow-hidden"
        style={{ background: '#111c30', border: '1px solid #1e2c44' }}
      >
        {/* Header strip — dynamic */}
        <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
          <span
            className="px-2 py-0.5 rounded text-[10px] font-bold font-mono shrink-0"
            style={epDef.method === 'POST'
              ? { background: 'rgba(52,211,153,.13)', color: '#34d399' }
              : { background: 'rgba(96,165,250,.15)', color: '#60a5fa' }
            }
          >
            {epDef.method}
          </span>
          <span className="text-[11px] font-mono text-[#8aa0c2] truncate flex-1" title={`${apiBase}${dynamicPath}`}>
            {dynamicPath}
          </span>
        </div>

        <div className="p-4 space-y-4">
          {/* Connection + Workflow — always shown */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => void testConnection()}
              disabled={loading !== null || !hasCreds}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-medium transition-colors disabled:opacity-40 shrink-0"
              style={{ background: 'rgba(52,211,153,.1)', border: '1px solid rgba(52,211,153,.25)', color: '#34d399' }}
            >
              {loading === 'conn'
                ? <RefreshCw size={11} className="animate-spin" />
                : connStatus === 'ok' ? <Wifi size={11} />
                : connStatus === 'error' ? <WifiOff size={11} className="text-red-400" />
                : <Wifi size={11} />}
              {loading === 'conn' ? 'Checking…' : connStatus === 'ok' ? 'Connected' : connStatus === 'error' ? 'Failed' : 'Test'}
            </button>

            <ComboSelect
              value={selectedWorkflow}
              onChange={setSelectedWorkflow}
              options={workflows.map(w => ({ value: w.workflowName }))}
              placeholder={loading === 'wf' ? 'Loading…' : 'Workflow name'}
              className="flex-1 text-[11px] text-[#94a3b8] font-mono rounded-[7px] px-2 py-1.5 focus:outline-none focus:border-[#6366f1]/50"
              style={{ background: '#0a1322', border: '1px solid #233149' }}
            />
          </div>

          {/* ── create-execute: full form ── */}
          {(selectedEndpoint === 'create-execute' || selectedEndpoint === 'create') && (
            <>
              {/* Persona chips */}
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-[#3f4f6b] mb-2">Test Persona · Auto-fill</p>
                <div className="flex flex-wrap gap-1.5">
                  {KYC_SCENARIOS.map((s, i) => {
                    const isActive = scenarioIdx === i
                    const { name, badge, docType } = parsePersonaChip(s.label)
                    return (
                      <button
                        key={i}
                        onClick={() => selectPersona(i)}
                        className="flex items-center gap-1.5 px-2.5 py-[5px] rounded-[8px] text-[11px] font-medium transition-all"
                        style={isActive
                          ? { background: 'rgba(99,102,241,.16)', border: '1px solid rgba(99,102,241,.45)', color: '#e2e8f0' }
                          : { background: '#0f1a2e', border: '1px solid #233149', color: '#94a3b8' }
                        }
                      >
                        <span className="w-[6px] h-[6px] rounded-full shrink-0" style={{ background: resultDotColor(s.expectedResult) }} />
                        <span>{name}</span>
                        {docType && <span className="text-[9px] text-[#4f6080]">({docType})</span>}
                        <span
                          className="text-[9px] font-bold px-1 py-0.5 rounded"
                          style={{
                            background: s.expectedResult === 'PASS' ? 'rgba(52,211,153,.15)' : s.expectedResult === 'FAIL' ? 'rgba(248,113,113,.15)' : 'rgba(251,146,60,.15)',
                            color: s.expectedResult === 'PASS' ? '#34d399' : s.expectedResult === 'FAIL' ? '#f87171' : '#fb923c',
                          }}
                        >
                          {badge}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Collapsible individual form */}
              <div>
                <button onClick={() => setFormOpen(o => !o)} className="w-full flex items-center justify-between py-1.5">
                  <SectionLabel>Individual data</SectionLabel>
                  <div className="flex items-center gap-2">
                    {scenarioIdx !== null && <span className="text-[10px] text-[#5b6b85]">{KYC_SCENARIOS[scenarioIdx].label.split('—')[1]?.trim()}</span>}
                    {formOpen ? <ChevronUp size={13} className="text-[#5b6b85]" /> : <ChevronDown size={13} className="text-[#5b6b85]" />}
                  </div>
                </button>

                {formOpen && (
                  <div className="space-y-3 pt-1">
                    <div>
                      <SectionLabel>Personal Details</SectionLabel>
                      <div className="grid grid-cols-3 gap-2">
                        {[{ label: 'Given Name', key: 'givenName' as const }, { label: 'Middle Name', key: 'middleName' as const }, { label: 'Family Name', key: 'familyName' as const }].map(f => (
                          <div key={f.key}>
                            <label className={labelCls}>{f.label}</label>
                            <input className={inputCls} value={form[f.key]} onChange={e => updateForm(ff => ({ ...ff, [f.key]: e.target.value }))} />
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {[{ label: 'DOB Year', key: 'dobYear' as const, ph: 'YYYY' }, { label: 'Month', key: 'dobMonth' as const, ph: 'MM' }, { label: 'Day', key: 'dobDay' as const, ph: 'DD' }, { label: 'Nationality', key: 'nationality' as const, ph: 'AUS' }].map(f => (
                          <div key={f.key}>
                            <label className={labelCls}>{f.label}</label>
                            <input className={inputCls} placeholder={f.ph} value={form[f.key]} onChange={e => updateForm(ff => ({ ...ff, [f.key]: e.target.value }))} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <SectionLabel>Address</SectionLabel>
                      <div className="grid grid-cols-3 gap-2">
                        {[{ label: 'Street No.', key: 'addressStreetNumber' as const }, { label: 'Street Name', key: 'addressStreetName' as const }, { label: 'Street Type', key: 'addressStreetType' as const, ph: 'STREET' }].map(f => (
                          <div key={f.key}>
                            <label className={labelCls}>{f.label}</label>
                            <input className={inputCls} placeholder={'ph' in f ? f.ph : ''} value={form[f.key]} onChange={e => updateForm(ff => ({ ...ff, [f.key]: e.target.value }))} />
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {[{ label: 'Suburb', key: 'addressLocality' as const }, { label: 'State', key: 'addressSubdivision' as const }, { label: 'Postcode', key: 'addressPostalCode' as const }, { label: 'Country', key: 'addressCountry' as const }].map(f => (
                          <div key={f.key}>
                            <label className={labelCls}>{f.label}</label>
                            <input className={inputCls} value={form[f.key]} onChange={e => updateForm(ff => ({ ...ff, [f.key]: e.target.value }))} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <SectionLabel>Identity Document</SectionLabel>
                      <div className="mb-2">
                        <label className={labelCls}>Document Type</label>
                        <select className={inputCls} value={form.docType} onChange={e => updateForm(f => ({ ...f, docType: e.target.value }))}>
                          {[{ v: 'PASSPORT', label: 'Passport' }, { v: 'DRIVERS_LICENSE', label: "Driver's License" }, { v: 'NATIONAL_HEALTH_ID', label: 'Medicare Card' }, { v: 'NATIONAL_ID', label: 'National ID' }, { v: 'BIRTH_CERT', label: 'Birth Certificate' }].map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                        </select>
                      </div>
                      {form.docType === 'PASSPORT' && (
                        <div className="grid grid-cols-2 gap-2">
                          <div><label className={labelCls}>Passport Number</label><input className={inputCls} value={form.docNumber} onChange={e => updateForm(f => ({ ...f, docNumber: e.target.value }))} /></div>
                          <div><label className={labelCls}>Country</label><input className={inputCls} value={form.docCountry} onChange={e => updateForm(f => ({ ...f, docCountry: e.target.value }))} /></div>
                        </div>
                      )}
                      {form.docType === 'DRIVERS_LICENSE' && (
                        <div className="grid grid-cols-3 gap-2">
                          <div><label className={labelCls}>License Number</label><input className={inputCls} value={form.docNumber} onChange={e => updateForm(f => ({ ...f, docNumber: e.target.value }))} /></div>
                          <div><label className={labelCls}>Card Number</label><input className={inputCls} value={form.docSecondaryIdentifier} onChange={e => updateForm(f => ({ ...f, docSecondaryIdentifier: e.target.value }))} /></div>
                          <div><label className={labelCls}>State</label><input className={inputCls} value={form.docSubdivision} onChange={e => updateForm(f => ({ ...f, docSubdivision: e.target.value }))} /></div>
                        </div>
                      )}
                      {form.docType === 'NATIONAL_HEALTH_ID' && (
                        <div className="grid grid-cols-3 gap-2">
                          <div><label className={labelCls}>Card Number</label><input className={inputCls} value={form.docNumber} onChange={e => updateForm(f => ({ ...f, docNumber: e.target.value }))} /></div>
                          <div><label className={labelCls}>Reference (position)</label><input className={inputCls} placeholder="1–9" value={form.docSecondaryIdentifier} onChange={e => updateForm(f => ({ ...f, docSecondaryIdentifier: e.target.value }))} /></div>
                          <div><label className={labelCls}>Country</label><input className={inputCls} value={form.docCountry} onChange={e => updateForm(f => ({ ...f, docCountry: e.target.value }))} /></div>
                        </div>
                      )}
                      {form.docType === 'NATIONAL_ID' && (
                        <div className="grid grid-cols-2 gap-2">
                          <div><label className={labelCls}>Document Number</label><input className={inputCls} value={form.docNumber} onChange={e => updateForm(f => ({ ...f, docNumber: e.target.value }))} /></div>
                          <div><label className={labelCls}>Country</label><input className={inputCls} value={form.docCountry} onChange={e => updateForm(f => ({ ...f, docCountry: e.target.value }))} /></div>
                        </div>
                      )}
                      {form.docType === 'BIRTH_CERT' && (
                        <div className="grid grid-cols-3 gap-2">
                          <div><label className={labelCls}>Registration Number</label><input className={inputCls} value={form.docNumber} onChange={e => updateForm(f => ({ ...f, docNumber: e.target.value }))} /></div>
                          <div><label className={labelCls}>State</label><input className={inputCls} value={form.docSubdivision} onChange={e => updateForm(f => ({ ...f, docSubdivision: e.target.value }))} /></div>
                          <div><label className={labelCls}>Country</label><input className={inputCls} value={form.docCountry} onChange={e => updateForm(f => ({ ...f, docCountry: e.target.value }))} /></div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1.5 text-[11px] text-[#94a3b8] cursor-pointer">
                        <input type="checkbox" checked={form.consentGeneral} onChange={e => updateForm(f => ({ ...f, consentGeneral: e.target.checked }))} style={{ accentColor: '#6366f1' }} />
                        GENERAL consent
                      </label>
                      <label className="flex items-center gap-1.5 text-[11px] text-[#94a3b8] cursor-pointer">
                        <input type="checkbox" checked={form.consentDocs} onChange={e => updateForm(f => ({ ...f, consentDocs: e.target.checked }))} style={{ accentColor: '#6366f1' }} />
                        DOCS consent
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── execute: entityId input + workflow ── */}
          {selectedEndpoint === 'execute' && (
            <div className="space-y-2">
              <div>
                <label className={labelCls}>Entity ID <span style={{ color: '#f87171' }}>*</span></label>
                <input className={inputCls} value={getEntityIdInput} onChange={e => setGetEntityIdInput(e.target.value)} placeholder="auto-filled from prior result" />
              </div>
            </div>
          )}

          {/* ── get-result: entityId + executionId inputs ── */}
          {selectedEndpoint === 'get-result' && (
            <div className="space-y-2">
              <div>
                <label className={labelCls}>Entity ID <span style={{ color: '#f87171' }}>*</span></label>
                <input className={inputCls} value={getEntityIdInput} onChange={e => setGetEntityIdInput(e.target.value)} placeholder="auto-filled from prior result" />
              </div>
              <div>
                <label className={labelCls}>Execution ID <span style={{ color: '#f87171' }}>*</span></label>
                <input className={inputCls} value={getExecutionIdInput} onChange={e => setGetExecutionIdInput(e.target.value)} placeholder="auto-filled from prior result" />
              </div>
            </div>
          )}

          {/* ── get-entity: entityId input ── */}
          {selectedEndpoint === 'get-entity' && (
            <div>
              <label className={labelCls}>Entity ID <span style={{ color: '#f87171' }}>*</span></label>
              <input className={inputCls} value={getEntityIdInput} onChange={e => setGetEntityIdInput(e.target.value)} placeholder="auto-filled from prior result" />
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-2 pt-1">
            {selectedEndpoint === 'create-execute' && (
              <button
                onClick={() => void createAndExecute()}
                disabled={loading !== null || !hasCreds || !selectedWorkflow}
                className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
              >
                {loading === 'exec'
                  ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Executing…</span>
                  : 'Create + Execute Workflow'
                }
              </button>
            )}

            {selectedEndpoint === 'create' && (
              <button
                onClick={() => void createOnly()}
                disabled={loading !== null || !hasCreds}
                className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
              >
                {loading === 'create' ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Creating…</span> : 'Create Entity'}
              </button>
            )}

            {selectedEndpoint === 'execute' && (
              <button
                onClick={() => void executeOnExisting()}
                disabled={loading !== null || !hasCreds || !getEntityIdInput || !selectedWorkflow}
                className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
              >
                {loading === 'execwf' ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Executing…</span> : 'Execute Workflow'}
              </button>
            )}

            {selectedEndpoint === 'get-result' && (
              <button
                onClick={() => void fetchWorkflowResult()}
                disabled={loading !== null || !hasCreds || !getEntityIdInput || !getExecutionIdInput || !selectedWorkflow}
                className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
              >
                {loading === 'getresult' ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Fetching…</span> : 'Get Result'}
              </button>
            )}

            {selectedEndpoint === 'get-entity' && (
              <button
                onClick={() => void getEntityDetails()}
                disabled={loading !== null || !hasCreds || !getEntityIdInput}
                className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
              >
                {loading === 'getentity' ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Fetching…</span> : 'Get Entity'}
              </button>
            )}

            {!hasCreds && <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>}
            {hasCreds && !selectedWorkflow && workflows.length === 0 && loading !== 'wf' && (
              <p className="text-[10px] text-amber-400 text-center">No workflows found — check credentials and retry</p>
            )}

            {/* Entity ID badge */}
            {entityId && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
                <span className="text-[10px] font-semibold text-emerald-400">entityId</span>
                <span className="text-[10px] font-mono text-[#94a3b8] flex-1 truncate">{entityId}</span>
                <CopyButton text={entityId} />
                <button onClick={() => { setEntityId(null); setWorkflowResult(null); setFullResult(null) }} className="text-[#5b6b85] hover:text-red-400 transition-colors">
                  <X size={10} />
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── COLUMN 3: Result Area ── */}
      <div className="space-y-3 min-w-0">
        {/* PayloadEditor + cURL — above webhooks */}
        {!isGetEndpoint && (
          <PayloadEditor
            value={editorText}
            dirty={editorDirty}
            error={editorError}
            onChange={handleEditorChange}
            onReset={resetEditor}
          />
        )}
        <CurlCollapsible
          method={epDef.method}
          url={`${apiBase}${dynamicPath}`}
          creds={creds}
          body={isGetEndpoint ? undefined : (editorDirty ? ((() => { try { return JSON.parse(editorText) as Record<string, unknown> } catch { return createAndExecPayload } })()) : createAndExecPayload)}
        />

        {/* Inline Webhooks — always visible */}
        <InlineWebhookPanel
          entityId={entityId}
          customerId={creds.customerId || null}
          onEventsChange={(evts) => setWebhookReceived(evts.length > 0)}
        />

        {/* Idle state */}
        {!isRunning && !workflowResult && !fullResult && (
          <div
            className="flex flex-col items-center justify-center py-10 rounded-[14px]"
            style={{ border: '2px dashed #233149', background: '#0d1626' }}
          >
            <div className="w-12 h-12 rounded-[12px] flex items-center justify-center mb-4" style={{ background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.2)' }}>
              <Zap size={22} className="text-[#6366f1]" />
            </div>
            <p className="text-[14px] font-semibold text-[#5b6b85]">Run a workflow to see the journey</p>
            <p className="text-[11px] text-[#3f4f6b] mt-1.5 text-center max-w-xs">Select a test persona or fill in the form, then click the action button</p>
          </div>
        )}

        {/* Running state */}
        {isRunning && (
          <div className="p-5 rounded-[14px] space-y-4" style={{ background: '#0e1828', border: '1px solid #2a3754' }}>
            <div className="flex items-center gap-3">
              <Loader2 size={18} className="animate-spin text-amber-400 shrink-0" />
              <div>
                {pollAttempt === 0 ? (
                  <p className="text-[13px] font-semibold text-[#fcd34d]">Waiting for response…</p>
                ) : (
                  <>
                    <p className="text-[13px] font-semibold text-[#fcd34d]">Polling for result… attempt {pollAttempt}/20</p>
                    <p className="text-[11px] font-mono text-[#5b6b85] mt-0.5">Workflow accepted · polling every 4s</p>
                  </>
                )}
              </div>
            </div>
            {pollAttempt > 0 && (
              <div className="h-[5px] rounded-full" style={{ background: '#16223a' }}>
                <div className="h-full rounded-full transition-all duration-300" style={{ width: `${Math.min((pollAttempt / 20) * 100, 100)}%`, background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
              </div>
            )}
            {steps.order?.length > 0 && (
              <div className="pt-1">
                <JourneyVisualization
                  view="flowchart"
                  steps={steps}
                  workflowStepResults={stepResults}
                  isRunning
                  selectedStep={selectedStep}
                  onStepClick={setSelectedStep}
                  entityId={entityId}
                  workflowName={selectedWorkflow}
                  workflowExecutionId={wfeId}
                  webhookReceived={webhookReceived}
                />
              </div>
            )}
          </div>
        )}

        {/* Result state — workflow result */}
        {!isRunning && workflowResult && (
          <div className="space-y-3" style={{ animation: 'fadeUp .25s ease' }}>
            <div className="p-4 rounded-[14px]" style={{ background: oc.bg, border: `1px solid ${oc.border}` }}>
              <div className="flex items-start gap-4">
                <div className="w-[46px] h-[46px] rounded-[12px] flex items-center justify-center shrink-0" style={{ background: `${oc.icon}20`, border: `1px solid ${oc.border}` }}>
                  {outcome === 'PASS' && <Check size={22} style={{ color: oc.icon }} />}
                  {outcome === 'FAIL' && <X size={22} style={{ color: oc.icon }} />}
                  {outcome === 'REFER' && <AlertTriangle size={22} style={{ color: oc.icon }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-[19px] font-extrabold" style={{ color: oc.text }}>{outcome}</span>
                    {workflowResult.riskAssessment && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-[6px]" style={{ background: `${oc.icon}20`, color: oc.text }}>
                        RISK {workflowResult.riskAssessment.riskLevel} · {workflowResult.riskAssessment.riskScore ?? '—'}
                      </span>
                    )}
                  </div>
                  {entityId && (
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] text-[#5b6b85]">entityId</span>
                      <span className="text-[10px] font-mono text-[#94a3b8] truncate">{entityId}</span>
                      <CopyButton text={entityId} />
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-[#475569] shrink-0">Flowchart</span>
              </div>
            </div>

            <div className="p-4 rounded-[14px]" style={{ background: '#0e1828', border: '1px solid #1e2c44' }}>
              <JourneyVisualization
                view="flowchart"
                steps={steps}
                workflowStepResults={stepResults}
                isRunning={false}
                selectedStep={selectedStep}
                onStepClick={setSelectedStep}
                entityId={entityId}
                workflowName={selectedWorkflow}
                workflowExecutionId={wfeId}
                overallStatus={outcome}
                webhookReceived={webhookReceived}
              />
            </div>

            {selectedStep && <StepDetailDrawer selectedStep={selectedStep} workflowStepResults={stepResults} />}

            <div className="grid grid-cols-2 gap-3">
              <KycCheckSummary wr={workflowResult} />
              <RiskFactorsPanel wr={workflowResult} />
            </div>

            <IssuesPanel wr={workflowResult} />
            <RawJsonCollapsible data={fullResult} />
          </div>
        )}

        {/* GET endpoint result — raw JSON */}
        {!workflowResult && fullResult && (
          <div style={{ animation: 'fadeUp .25s ease' }}>
            <RawJsonCollapsible data={fullResult} />
          </div>
        )}
      </div>
    </div>
  )
}
